import { masterSupabase } from '../lib/supabase-master.js';
import documentAccessService from '../services/documentAccessService.js';

/**
 * Document Manager for Clara360 Marketplace
 * Handles photo uploads, document selection, and file management
 */
class DocumentManager {
  constructor() {
    this.allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    this.allowedDocumentTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    this.maxFileSize = 10 * 1024 * 1024; // 10MB
    this.maxImagesPerProperty = 20;
    
    this.documentCategories = {
      photos: {
        name: 'Fotos',
        icon: '📸',
        subcategories: ['exterior', 'living_room', 'kitchen', 'bathroom', 'bedroom', 'other'],
        required: true
      },
      floorplans: {
        name: 'Grundrisse',
        icon: '📐',
        subcategories: ['floor_plan', 'layout'],
        required: false
      },
      certificates: {
        name: 'Zertifikate',
        icon: '📋',
        subcategories: ['energy_certificate', 'building_permit', 'other'],
        required: false
      },
      legal: {
        name: 'Rechtsdokumente',
        icon: '⚖️',
        subcategories: ['house_rules', 'rental_agreement_template', 'other'],
        required: false
      }
    };
  }

  /**
   * Get existing documents for property/unit
   */
  async getPropertyDocuments(propertyId, unitId = null) {
    try {
      let query = masterSupabase
        .from('documents')
        .select('*')
        .eq('property_id', propertyId)
        .order('created_at', { ascending: false });

      if (unitId) {
        query = query.eq('unit_id', unitId);
      }

      const { data, error } = await query;

      if (error) {
        throw new Error('Fehler beim Laden der Dokumente: ' + error.message);
      }

      return this.categorizeDocuments(data || []);
    } catch (error) {
      console.error('Error loading property documents:', error);
      throw error;
    }
  }

  /**
   * Categorize documents by type and subcategory
   */
  categorizeDocuments(documents) {
    const categorized = {};
    
    Object.keys(this.documentCategories).forEach(category => {
      categorized[category] = {
        ...this.documentCategories[category],
        documents: [],
        count: 0
      };
    });

    documents.forEach(doc => {
      const category = doc.category || 'photos';
      if (categorized[category]) {
        categorized[category].documents.push(doc);
        categorized[category].count++;
      }
    });

    return categorized;
  }

  /**
   * Upload new document/photo
   */
  async uploadDocument(file, propertyId, unitId, category, subcategory, metadata = {}) {
    try {
      // Validate file
      this.validateFile(file, category);

      const document = await documentAccessService.uploadDocument(file, {
        property_id: propertyId,
        unit_id: unitId,
        category,
        document_type: subcategory || category,
        description: metadata?.description || '',
      });
      return {
        ...document,
        category,
        subcategory,
        file_url: document.viewer_url || null,
      };
    } catch (error) {
      console.error('Error uploading document:', error);
      throw error;
    }
  }

  /**
   * Upload multiple photos with progress tracking
   */
  async uploadMultiplePhotos(files, propertyId, unitId, onProgress = null) {
    const results = [];
    const errors = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      try {
        // Auto-categorize photo based on filename or metadata
        const subcategory = this.autoCategorizPhoto(file.name);
        
        const result = await this.uploadDocument(
          file, 
          propertyId, 
          unitId, 
          'photos', 
          subcategory,
          { auto_categorized: true }
        );
        
        results.push(result);
        
        if (onProgress) {
          onProgress({
            completed: i + 1,
            total: files.length,
            currentFile: file.name,
            success: true
          });
        }
      } catch (error) {
        errors.push({ file: file.name, error: error.message });
        
        if (onProgress) {
          onProgress({
            completed: i + 1,
            total: files.length,
            currentFile: file.name,
            success: false,
            error: error.message
          });
        }
      }
    }

    return { results, errors };
  }

  /**
   * Auto-categorize photo based on filename
   */
  autoCategorizPhoto(filename) {
    const name = filename.toLowerCase();
    
    if (name.includes('außen') || name.includes('fassade') || name.includes('exterior')) {
      return 'exterior';
    }
    if (name.includes('wohnzimmer') || name.includes('living')) {
      return 'living_room';
    }
    if (name.includes('küche') || name.includes('kitchen')) {
      return 'kitchen';
    }
    if (name.includes('bad') || name.includes('bathroom') || name.includes('wc')) {
      return 'bathroom';
    }
    if (name.includes('schlafzimmer') || name.includes('bedroom')) {
      return 'bedroom';
    }
    
    return 'other';
  }

  /**
   * Validate uploaded file
   */
  validateFile(file, category) {
    // Check file size
    if (file.size > this.maxFileSize) {
      throw new Error(`Datei zu groß. Maximum: ${this.maxFileSize / 1024 / 1024}MB`);
    }

    // Check file type
    const allowedTypes = category === 'photos' ? this.allowedImageTypes : this.allowedDocumentTypes;
    if (!allowedTypes.includes(file.type)) {
      throw new Error(`Dateityp nicht unterstützt: ${file.type}`);
    }

    // Check filename
    if (!file.name || file.name.length > 255) {
      throw new Error('Ungültiger Dateiname');
    }
  }

  /**
   * Generate unique filename
   */
  generateFileName(file, propertyId, unitId, category) {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    const extension = file.name.split('.').pop().toLowerCase();
    const unitPrefix = unitId ? `unit_${unitId.slice(0, 8)}_` : '';
    
    return `${category}_${propertyId.slice(0, 8)}_${unitPrefix}${timestamp}_${random}.${extension}`;
  }

  /**
   * Delete document
   */
  async deleteDocument(documentId) {
    try {
      const result = await documentAccessService.deleteDocument(documentId);
      return { success: result.deleted === true };
    } catch (error) {
      console.error('Error deleting document:', error);
      throw error;
    }
  }

  /**
   * Get optimized image URL for different use cases
   */
  getOptimizedImageUrl() {
    // Persistierte Datei-URLs sind keine Autoritätsquelle mehr. Marketplace-
    // Thumbnails werden über die zentrale, kurzlebige Zugriffsschicht geladen.
    return null;
  }

  /**
   * Generate document suggestions for exposé
   */
  generateDocumentSuggestions(propertyType, unitType) {
    const suggestions = {
      required: [],
      recommended: [],
      optional: []
    };

    // Required documents
    suggestions.required.push(
      { category: 'photos', subcategory: 'exterior', name: 'Außenansicht' },
      { category: 'photos', subcategory: 'living_room', name: 'Wohnbereich' }
    );

    // Recommended based on property type
    if (propertyType === 'Mehrfamilienhaus') {
      suggestions.recommended.push(
        { category: 'photos', subcategory: 'kitchen', name: 'Küche' },
        { category: 'photos', subcategory: 'bathroom', name: 'Badezimmer' },
        { category: 'floorplans', subcategory: 'floor_plan', name: 'Grundriss' }
      );
    }

    if (propertyType === 'Gewerbeimmobilie') {
      suggestions.recommended.push(
        { category: 'photos', subcategory: 'other', name: 'Arbeitsbereich' },
        { category: 'floorplans', subcategory: 'layout', name: 'Raumaufteilung' }
      );
    }

    // Optional documents
    suggestions.optional.push(
      { category: 'certificates', subcategory: 'energy_certificate', name: 'Energieausweis' },
      { category: 'legal', subcategory: 'house_rules', name: 'Hausordnung' }
    );

    return suggestions;
  }

  /**
   * Prepare documents for PDF export
   */
  async prepareDocumentsForExport(selectedDocuments) {
    const exportData = {
      photos: [],
      floorplans: [],
      certificates: [],
      legal: []
    };

    for (const doc of selectedDocuments) {
      try {
        // Get optimized version for PDF
        const optimizedUrl = this.getOptimizedImageUrl(doc, 'medium');
        
        exportData[doc.category] = exportData[doc.category] || [];
        exportData[doc.category].push({
          ...doc,
          optimizedUrl,
          displayName: this.getDisplayName(doc)
        });
      } catch (error) {
        console.warn('Could not prepare document for export:', doc.id, error);
      }
    }

    return exportData;
  }

  /**
   * Get display name for document
   */
  getDisplayName(document) {
    const subcategoryNames = {
      exterior: 'Außenansicht',
      living_room: 'Wohnzimmer',
      kitchen: 'Küche',
      bathroom: 'Badezimmer',
      bedroom: 'Schlafzimmer',
      floor_plan: 'Grundriss',
      layout: 'Raumaufteilung',
      energy_certificate: 'Energieausweis',
      house_rules: 'Hausordnung'
    };

    return subcategoryNames[document.subcategory] || document.original_name || 'Dokument';
  }

  /**
   * Get document statistics
   */
  getDocumentStats(categorizedDocuments) {
    const stats = {
      totalDocuments: 0,
      totalPhotos: 0,
      totalSize: 0,
      completeness: 0,
      missingRequired: []
    };

    Object.values(categorizedDocuments).forEach(category => {
      stats.totalDocuments += category.count;
      if (category.name === 'Fotos') {
        stats.totalPhotos = category.count;
      }
      
      category.documents.forEach(doc => {
        stats.totalSize += doc.file_size || 0;
      });
    });

    // Calculate completeness score
    const requiredCategories = Object.entries(this.documentCategories)
      .filter(([key, cat]) => cat.required);
    
    let completedRequired = 0;
    requiredCategories.forEach(([key, cat]) => {
      if (categorizedDocuments[key] && categorizedDocuments[key].count > 0) {
        completedRequired++;
      } else {
        stats.missingRequired.push(cat.name);
      }
    });

    stats.completeness = requiredCategories.length > 0 
      ? Math.round((completedRequired / requiredCategories.length) * 100)
      : 100;

    return stats;
  }
}

export default DocumentManager;

